import * as st from '../../core/st';

// Element color definitions
const ELEMENT_COLORS = {
    STRENGTH: 'rgb(225, 143, 50)',
    INTELLIGENCE: 'rgb(236, 71, 71)',
    CHANCE: 'rgb(67, 128, 219)',
    AGILITY: 'rgb(65, 199, 83)',
    POWER: 'rgb(219, 202, 91)',
    NONE: 'rgb(124, 124, 124)'
};

// Dominance calculation parameters
const POWER_EXPONENT = 3;
const STRONG_THRESHOLD = 0.3;
const WEAK_MULTIPLIER = 0.3;
const MAX_DOMINANCE = 0.9;

// Stat similarity calculation
const SENSITIVITY_MULTIPLIER = 5;
const MIN_SIMILARITY = 0;
const MAX_SIMILARITY = 1;
const MAX_FLATTENING_SIMILARITY = 0.05; // Cap similarity to prevent full gray when stats are equal

// Color normalization
const TARGET_SATURATION = 0.5;
const RGB_NORMALIZATION = true;

// Helper function to convert RGB to HSL
const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: h = 0; break;
        }
        h /= 6;
    }

    return { h, s, l };
};

// Helper function to convert HSL to RGB
const hslToRgb = (h, s, l) => {
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
        r: r * 255,
        g: g * 255,
        b: b * 255
    };
};

// Function to normalize saturation while preserving hue and lightness
const normalizeSaturation = (r, g, b) => {
    if (!RGB_NORMALIZATION) {
        return { r, g, b };
    }

    // Convert RGB to HSL
    const hsl = rgbToHsl(r, g, b);

    // Set a target saturation for consistent visual impact
    const targetSaturation = TARGET_SATURATION;

    // Convert back to RGB with normalized saturation
    const rgb = hslToRgb(hsl.h, 0.5 * (targetSaturation + hsl.s), hsl.l);

    return {
        r: Math.round(rgb.r),
        g: Math.round(rgb.g),
        b: Math.round(rgb.b)
    };
};

// Calculate how similar the four main stats are (0 = all different, 1 = all equal)
const calculateStatSimilarity = (elementData) => {
    const mainStats = elementData.filter(element =>
        element.id === st.STRENGTH_ID ||
        element.id === st.INTELLIGENCE_ID ||
        element.id === st.CHANCE_ID ||
        element.id === st.AGILITY_ID
    );

    if (mainStats.length < 2) return 0;

    // Get normalized values for the main stats
    const values = mainStats.map(stat => stat.normalizedValue);

    // Calculate standard deviation to measure similarity
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);

    // Convert to similarity (0 = high variance, 1 = no variance)
    // Use exponential decay to make it more sensitive to small differences
    const similarity = Math.exp(-stdDev * SENSITIVITY_MULTIPLIER);

    return Math.max(MIN_SIMILARITY, Math.min(MAX_SIMILARITY, similarity));
};

// Flatten RGB values based on stat similarity
const flattenRGBBySimilarity = (r, g, b, similarity) => {
    if (similarity <= 0) {
        return { r, g, b }; // No flattening when stats are very different
    }

    // Cap similarity to prevent full gray when stats are perfectly equal
    const cappedSimilarity = Math.min(similarity, MAX_FLATTENING_SIMILARITY);

    // Calculate the average RGB value
    const avgRGB = Math.round((r + g + b) / 3);

    // Interpolate between original color and gray based on capped similarity
    const flattenedR = Math.round(r * (1 - cappedSimilarity) + avgRGB * cappedSimilarity);
    const flattenedG = Math.round(g * (1 - cappedSimilarity) + avgRGB * cappedSimilarity);
    const flattenedB = Math.round(b * (1 - cappedSimilarity) + avgRGB * cappedSimilarity);

    return { r: flattenedR, g: flattenedG, b: flattenedB };
};

/**
 * Get stat value from stats object, handling both formats:
 * - Simple format: { [statId]: value }
 * - Complex format: { [statId]: { current: value } }
 */
const getStatValue = (stat) => {
    if (stat === undefined || stat === null) return 0;
    // If it's a number, return directly (simple format)
    if (typeof stat === 'number') return stat;
    // If it's an object with 'current' property, use that
    if (typeof stat === 'object' && 'current' in stat) return stat.current || 0;
    // Fallback: try to use the value directly if it's a number
    return typeof stat === 'number' ? stat : 0;
};

/** True for an already-resolved {r,g,b}, as opposed to a stat sheet. */
const isResolvedColor = (value) => (
    !!value
    && typeof value === 'object'
    && Number.isFinite(value.r)
    && Number.isFinite(value.g)
    && Number.isFinite(value.b)
);

/**
 * Calculate blended RGB color from stats
 * @param {Object} stats - Stats object. Accepts:
 *   - Simple format: { [statId]: value }
 *   - Complex format: { [statId]: { current: value } }
 *   - An already-resolved { r, g, b }, returned as-is.
 *
 * The last form is what lets a build card skip the stat sheet entirely. The
 * colour is derived from equipment, so drawing a card used to mean shipping all
 * 16 items and re-deriving stats for each of 48 cards. Builds now carry their
 * colour (Build.getElementColor), and accepting it here means the whole gradient
 * API keeps working unchanged rather than every one of its ~48 call sites
 * needing to know which form it holds.
 * @returns {{r: number, g: number, b: number}} RGB color values
 */
export const calculateBlendedColor = (stats) => {
    if (isResolvedColor(stats)) {
        return { r: stats.r, g: stats.g, b: stats.b };
    }
    if (!stats) {
        // Return gray (NONE) if no stats
        const grayMatch = ELEMENT_COLORS.NONE.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (grayMatch) {
            const [, r, g, b] = grayMatch;
            return { r: parseInt(r), g: parseInt(g), b: parseInt(b) };
        }
        return { r: 80, g: 80, b: 80 };
    }

    // Check for POWER stat
    const powerStat = stats[st.POWER_ID];
    const powerValue = powerStat ? getStatValue(powerStat) : 0;

    // Define the element mappings
    const elements = [
        {
            id: st.STRENGTH_ID,
            name: 'strength',
            color: 'brown',
            rgb: ELEMENT_COLORS.STRENGTH
        },
        {
            id: st.INTELLIGENCE_ID,
            name: 'intelligence',
            color: 'red',
            rgb: ELEMENT_COLORS.INTELLIGENCE
        },
        {
            id: st.CHANCE_ID,
            name: 'chance',
            color: 'blue',
            rgb: ELEMENT_COLORS.CHANCE
        },
        {
            id: st.AGILITY_ID,
            name: 'agility',
            color: 'green',
            rgb: ELEMENT_COLORS.AGILITY
        }
    ];

    // Calculate normalized values and dominance for all elements
    const elementValues = elements.map(element => {
        const stat = stats[element.id];
        if (!stat) return { ...element, normalizedValue: 0, dominance: 0 };

        const currentValue = getStatValue(stat);
        return { ...element, normalizedValue: currentValue, currentValue };
    });

    // Find the highest value to use as 100% reference
    const maxValue = Math.max(...elementValues.map(elem => elem.normalizedValue));

    // Check if POWER is dominant (higher than max elemental stat)
    if (powerValue > maxValue && powerValue > 0) {
        // POWER is dominant, return POWER color
        const powerMatch = ELEMENT_COLORS.POWER.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (powerMatch) {
            const [, r, g, b] = powerMatch;
            return { r: parseInt(r), g: parseInt(g), b: parseInt(b) };
        }
    }

    // Calculate dominance based on relative strength to the highest value
    const elementData = elementValues.map(elem => {
        const relativeStrength = maxValue > 0 ? elem.normalizedValue / maxValue : 0;

        // Use a less aggressive exponential approach for reduced dominance
        let dominance;

        if (relativeStrength > STRONG_THRESHOLD) {
            // Strong elements get dominance proportional to their relative strength
            dominance = Math.pow(relativeStrength, POWER_EXPONENT);
        } else {
            // Weak elements get minimal dominance
            dominance = Math.pow(relativeStrength, POWER_EXPONENT) * WEAK_MULTIPLIER;
        }

        return {
            ...elem,
            dominance: Math.min(dominance, MAX_DOMINANCE)
        };
    });

    // Filter out elements with zero dominance
    const activeElements = elementData.filter(element => element.dominance > 0);

    if (activeElements.length === 0) {
        // No active elements - check if there's power, otherwise use gray
        if (powerValue > 0) {
            const powerMatch = ELEMENT_COLORS.POWER.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (powerMatch) {
                const [, r, g, b] = powerMatch;
                return { r: parseInt(r), g: parseInt(g), b: parseInt(b) };
            }
        }
        // Return gray (NONE) if no stats
        const grayMatch = ELEMENT_COLORS.NONE.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (grayMatch) {
            const [, r, g, b] = grayMatch;
            return { r: parseInt(r), g: parseInt(g), b: parseInt(b) };
        }
        return { r: 80, g: 80, b: 80 };
    }

    // Calculate stat similarity to determine RGB flattening
    const statSimilarity = calculateStatSimilarity(elementData);

    // Simple RGB blending
    let blendedR = 0, blendedG = 0, blendedB = 0;
    let totalDominance = 0;

    activeElements.forEach(element => {
        const rgbMatch = element.rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
            const [, r, g, b] = rgbMatch;

            // Weight by dominance
            blendedR += parseInt(r) * element.dominance;
            blendedG += parseInt(g) * element.dominance;
            blendedB += parseInt(b) * element.dominance;
            totalDominance += element.dominance;
        }
    });

    // Normalize by total dominance
    if (totalDominance > 0) {
        blendedR = Math.round(blendedR / totalDominance);
        blendedG = Math.round(blendedG / totalDominance);
        blendedB = Math.round(blendedB / totalDominance);
    }

    // Normalize saturation to ensure consistent visual impact
    const normalizedColor = normalizeSaturation(blendedR, blendedG, blendedB);
    blendedR = normalizedColor.r;
    blendedG = normalizedColor.g;
    blendedB = normalizedColor.b;

    // Flatten RGB values based on stat similarity
    const flattenedColor = flattenRGBBySimilarity(blendedR, blendedG, blendedB, statSimilarity);
    blendedR = flattenedColor.r;
    blendedG = flattenedColor.g;
    blendedB = flattenedColor.b;

    return { r: blendedR, g: blendedG, b: blendedB };
};

// Gamma correction constant for brighter colors
const GAMMA_VALUE = 0.7;

/**
 * Calculate a higher gamma (brighter) color from RGB values
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {{r: number, g: number, b: number}} Brightened RGB color values
 */
export const calculateHigherGammaColor = (r, g, b) => {
    // Apply gamma correction: output = (input / 255) ^ gamma * 255
    // Lower gamma value (< 1) makes colors brighter
    const gamma = GAMMA_VALUE;

    const brightenedR = Math.round(Math.pow(r / 255, gamma) * 255);
    const brightenedG = Math.round(Math.pow(g / 255, gamma) * 255);
    const brightenedB = Math.round(Math.pow(b / 255, gamma) * 255);

    return { r: brightenedR, g: brightenedG, b: brightenedB };
};
